import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { IUserState } from './state';
import * as AT from './actionTypes';
import * as MT from './mutationTypes';
import {
  OrganisationUser,
  UserRequest,
  DeleteUserRequest,
  Role,
  OrgUsersRequest,
  PaginatedResult,
  ClientPaginatedResult,
  OrgPensionSchemeRequest
} from '../types';
import { UserService } from '../services';
import { PensionScheme } from '@/modules/pensionScheme/types';
import { IAuthActionContext } from '@/modules/auth/store/actions';
import { SET_LOADER } from '../../organisation/store/mutationTypes';
import Organisation from '@/models/Organisation';
/**
 * --------------------------------------------------
 *  User Actions
 * --------------------------------------------------
 **/
export const UserActions: IUserActions = {
  [AT.FETCH_ORGANISATION_USERS]: (
    { commit },
    orgUsersRequest: OrgUsersRequest
  ): Promise<OrganisationUser[]> => {
    commit(SET_LOADER, true);
    return UserService.getOrganizationUsers(orgUsersRequest).then(
      (paginatedResult: PaginatedResult) => {
        commit(MT.SET_PAGINATION_META, paginatedResult.meta);
        commit(MT.SET_ORGANISATION_USERS, paginatedResult.data);
        commit(SET_LOADER, false);
        return paginatedResult.data;
      }
    );
  },
  [AT.FETCH_ORGANISATION_CLIENTS]: (
    { commit },
    params: OrgPensionSchemeRequest
  ): Promise<Organisation[]> => {
    commit(SET_LOADER, true);
    return UserService.getOrganizationClients(params).then(
      (paginatedResult: ClientPaginatedResult) => {
        commit(MT.SET_CLIENT_PAGINATION_META, paginatedResult.meta);
        commit(MT.SET_ORGANISATION_CLIENTS, paginatedResult.data);
        commit(SET_LOADER, false);
        return paginatedResult.data;
      }
    );
  },
  [AT.ADD_ORGANISATION_USER]: ({ commit }, userRequest): Promise<Response> => {
    return UserService.postOrganizationUser(userRequest).then(
      (data: Response) => data
    );
  },
  [AT.DELETE_ORGANISATION_USER]: (
    { commit },
    deleteUserRequest
  ): Promise<Response> => {
    return UserService.deleteOrganizationUser(deleteUserRequest).then(
      (data: Response) => data
    );
  },
  [AT.EDIT_ORGANISATION_USER]: ({ commit }, userRequest): Promise<Response> => {
    return UserService.putOrganizationUser(userRequest).then(
      (data: Response) => data
    );
  },
  [AT.FETCH_ROLES]: ({ commit }): Promise<Role[]> => {
    return UserService.getRoles().then((roles: Role[]) => {
      const allowedRoles = ['admin', 'manager', 'member', 'viewer'];
      const filteredRoles = roles.filter((role: Role) =>
        allowedRoles.includes(role.name)
      );
      commit(MT.SET_ROLES, filteredRoles);
      return filteredRoles;
    });
  }
};

/**
 * --------------------------------------------------
 *  User Action Context Interface
 * --------------------------------------------------
 **/
export interface IUserActionContext
  extends ActionContext<IUserState, IRootState> {}

/**
 * --------------------------------------------------
 *  User Actions Interface
 * --------------------------------------------------
 **/
export interface IUserActions extends ActionTree<IUserState, IRootState> {
  [AT.FETCH_ORGANISATION_USERS](
    context: IUserActionContext,
    orgUsersRequest: OrgUsersRequest
  ): Promise<OrganisationUser[]>;
  [AT.FETCH_ORGANISATION_CLIENTS](
    context: IUserActionContext,
    params: OrgPensionSchemeRequest
  ): Promise<Organisation[]>;
  [AT.ADD_ORGANISATION_USER](
    context: IUserActionContext,
    userRequest: UserRequest
  ): Promise<Response>;
  [AT.DELETE_ORGANISATION_USER](
    context: IUserActionContext,
    deleteUserRequest: DeleteUserRequest
  ): Promise<Response>;
  [AT.EDIT_ORGANISATION_USER](
    context: IUserActionContext,
    userRequest: UserRequest
  ): Promise<Response>;
  [AT.FETCH_ROLES](context: IAuthActionContext): Promise<Role[]>;
}
